export interface ActivatableVisitType {
  type_id: string;
  is_active?: boolean;
}

export interface NamedVisitType extends ActivatableVisitType {
  name?: string;
  name_ar?: string;
}

export interface ActivatableService {
  service_id: string;
  isActive?: boolean;
}

export interface CatalogPackage {
  package_id?: string;
  description?: string;
  package_origin?: string;
  origin?: string;
  catalog_visibility?: string;
  tracking_type?: string;
}

const INSURANCE_PACKAGE_DESCRIPTIONS = [
  'auto-created from insurance item:',
  'server-created from insurance item:',
];

const clean = (value: unknown): string => String(value ?? '').trim();

/** Internal insurance packages back patient session balances but are not a
 * doctor-managed catalog item that can be sold or assigned manually. */
export function isInsuranceTrackingPackage<T extends CatalogPackage>(pkg: T): boolean {
  const origin = clean(
    pkg.package_origin || pkg.origin || pkg.catalog_visibility || pkg.tracking_type
  ).toLowerCase();
  if (origin === 'insurance' || origin === 'insurance_internal') return true;

  if (clean(pkg.package_id).toLowerCase().startsWith('ins_pkg_')) return true;

  const description = clean(pkg.description).toLowerCase();
  return INSURANCE_PACKAGE_DESCRIPTIONS.some((prefix) => description.startsWith(prefix));
}

export function visibleManualPackages<T extends CatalogPackage>(packages: T[]): T[] {
  return packages.filter((pkg) => !isInsuranceTrackingPackage(pkg));
}

export function visibleAdditionalServices<T extends ActivatableService>(
  services: T[],
): T[] {
  return services.filter((service) => service.isActive !== false);
}

export function visibleCustomVisitTypes<T extends ActivatableVisitType>(
  visitTypes: T[],
): T[] {
  return visitTypes.filter((visitType) => visitType.is_active !== false);
}

export function normalizeCatalogName(value?: string): string {
  return (value || '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase();
}

export function findVisitTypeWithSameName<T extends NamedVisitType>(
  visitTypes: T[],
  name?: string,
  nameAr?: string,
  excludedTypeId = '',
): T | undefined {
  const normalizedName = normalizeCatalogName(name);
  const normalizedNameAr = normalizeCatalogName(nameAr);

  const matches = visitTypes.filter((visitType) => {
    if (visitType.type_id === excludedTypeId) return false;
    const sameEnglishName = normalizedName !== ''
      && normalizeCatalogName(visitType.name) === normalizedName;
    const sameArabicName = normalizedNameAr !== ''
      && normalizeCatalogName(visitType.name_ar) === normalizedNameAr;
    return sameEnglishName || sameArabicName;
  });

  // If legacy data already contains duplicates, never reactivate a hidden row
  // while an active row with the same name exists.
  return matches.find((visitType) => visitType.is_active !== false) || matches[0];
}

export function isActiveVisitTypeDuplicate(
  visitType?: ActivatableVisitType,
): boolean {
  return Boolean(visitType && visitType.is_active !== false);
}

export function setVisitTypeActive<T extends ActivatableVisitType>(
  visitTypes: T[],
  typeId: string,
  isActive: boolean,
): T[] {
  return visitTypes.map((visitType) => (
    visitType.type_id === typeId
      ? { ...visitType, is_active: isActive }
      : visitType
  ));
}

export function selectableVisitTypes<T extends ActivatableVisitType>(
  visitTypes: T[],
  currentTypeId = '',
): T[] {
  return visitTypes.filter((visitType) => (
    visitType.is_active !== false || visitType.type_id === currentTypeId
  ));
}

export function selectablePackageServices<T extends ActivatableService>(
  services: T[],
  existingServiceIds: string[] = [],
): T[] {
  const retainedIds = new Set(existingServiceIds);
  return services.filter((service) => (
    service.isActive !== false || retainedIds.has(service.service_id)
  ));
}
